<app-campaign-details-banner
  [adminMode]="adminMode"
  [campaign]="store.campaignApi$()"
  [campaignAnalytics]="store.campaignAnalyticsShell$()"
  [breadcrumbs]="breadcrumbs"
  [creatorMode]="creatorMode()"
  [creatorBriefingMode]="creatorBriefingMode()"
  [campaignExport]="store.campaignExport$()"
  [exportMode]="exportMode()"
  [analyticsNodes]="store.campaignAnalyticsShellNodes$()"
/>

<app-content-container class="w-fill-available relative">
  @if (store.limitReached$()) {
    <div
      class="absolute inset-0 w-full h-full backdrop-blur-sm bg-white/50 z-50 pointer-events-none flex items-center justify-center"
    >
      <div class="text-center px-4">
        <p class="text-lg font-semibold mb-2">{{ 'pages.campaignDetails.limitReached.title' | translate }}</p>
        <p class="text-sm text-shikenso-purple-base-60">
          {{ 'pages.campaignDetails.limitReached.message' | translate }}
        </p>
      </div>
    </div>
  }

  <div class="w-full mb-8">
    <app-campaign-details-filters
      class="tab-group-filters !relative !top-0 !z-0"
      [campaignAnalytics]="store.campaignAnalyticsShell$()"
      [campaignAnalyticsFilter]="campaignAnalyticsFilter()"
      [creatorBriefingMode]="creatorBriefingMode()"
      [dateFilterState]="dateFilterState()"
      [allowedDateRange]="exportDateRangeBounds()"
      [dateFilterReady]="dateFilterReady()"
      [loading]="filtersLoading()"
      [limitReached]="store.limitReached$()"
      (campaignAnalyticsFilterChanged)="onCampaignAnalyticsFilterChanged($event)"
      (dateRangeChanged)="onDateRangeChanged($event)"
      (selectedRangeChanged)="onSelectedRangeChanged($event)"
    />
  </div>

  @if (exportDataLoading()) {
    <mat-tab-group class="w-full profile-tab-group" [selectedIndex]="0">
      @for (tab of [1, 2, 3]; track tab) {
        <mat-tab [disabled]="true">
          <ng-template mat-tab-label>
            <ngx-skeleton-loader class="h-5" [theme]="{ width: '90px', height: '20px', 'border-radius': '8px' }" />
          </ng-template>
          <div class="pt-7 min-h-[calc(100vh-400px)] flex items-center justify-center">
            <app-dots-spinner />
          </div>
        </mat-tab>
      }
    </mat-tab-group>
  } @else if (creatorBriefingMode()) {
    <mat-tab-group
      class="w-full profile-tab-group briefing-tab-group"
      [class.overflow-hidden]="store.limitReached$()"
      [class.pointer-events-none]="store.limitReached$()"
      [selectedIndex]="activeTabIndex()"
      (selectedIndexChange)="onSelectedTabIndexChange($event)"
    >
      <mat-tab [disabled]="store.limitReached$()">
        <ng-template mat-tab-label>
          <span class="text-base" data-testid="campaign-details-tab-overview">{{
            'pages.campaignDetails.tabs.overview' | translate
          }}</span>
        </ng-template>
        <div class="pt-7 max-md:pt-6 min-h-[calc(100vh-400px)]" [class.overflow-hidden]="store.limitReached$()">
          <app-campaign-details-creator-overview
            data-testid="campaign-details-tab-panel-creator-overview"
            [campaign]="store.campaignApi$()"
            [campaignAnalytics]="store.campaignAnalyticsShell$()"
            [analyticsNodes]="deliverablesAnalyticsNodes()"
            [optionalAnalyticsNodes]="optionalAnalyticsNodes()"
            [ctaKpiStatus]="store.ctaKpiStatusState$().data"
            (tileClicked)="onTileClick($event)"
            (viewAllPlannedContentsClicked)="onViewAllPlannedContents()"
          />
        </div>
      </mat-tab>

      <mat-tab [disabled]="store.limitReached$()">
        <ng-template mat-tab-label>
          <span class="text-base" data-testid="campaign-details-tab-timeline">{{
            'pages.campaignDetails.tabs.timeline' | translate
          }}</span>
        </ng-template>
        <div class="pt-7 max-md:pt-6 min-h-[calc(100vh-400px)]" [class.overflow-hidden]="store.limitReached$()">
          <app-campaign-details-creator-timeline
            data-testid="campaign-details-tab-panel-timeline"
            [analyticsNodes]="deliverablesAnalyticsNodes()"
            [resetToPlanned]="selectPlannedTrigger()"
          />
        </div>
      </mat-tab>
    </mat-tab-group>
  } @else {
    <mat-tab-group
      class="w-full profile-tab-group"
      [class.overflow-hidden]="store.limitReached$()"
      [class.pointer-events-none]="store.limitReached$()"
      [selectedIndex]="activeTabIndex()"
      (selectedIndexChange)="onSelectedTabIndexChange($event)"
    >
      @if (visibleTabs().includes(ECampaignDetailsTab.overview)) {
        <mat-tab [disabled]="store.limitReached$()">
          <ng-template mat-tab-label>
            <span class="text-base" data-testid="campaign-details-tab-overview">{{
              'pages.campaignDetails.tabs.overview' | translate
            }}</span>
          </ng-template>
          <div class="pt-7 min-h-[calc(100vh-400px)]" [class.overflow-hidden]="store.limitReached$()">
            <app-campaign-details-overview
              data-testid="campaign-details-tab-panel-overview"
              [ctaKpiStatusData]="store.ctaKpiStatusState$().data"
              [analyticsNodes]="deliverablesAnalyticsNodes()"
              [loading]="!store.ctaKpiStatusState$().data || store.ctaKpiStatusState$().inFlight"
              [hasCtaKpiStatus]="(store.ctaKpiStatusState$().data?.length ?? 0) > 0"
              [campaignExportConfig]="campaignExportConfig()"
              (deepLinkClicked)="onOverviewDeepLinkClicked($event)"
            />
          </div>
        </mat-tab>
      }

      @if (visibleTabs().includes(ECampaignDetailsTab.kpiGoals)) {
        <mat-tab [disabled]="store.limitReached$()">
          <ng-template mat-tab-label>
            <span class="text-base" data-testid="campaign-details-tab-kpi-goals">{{
              'pages.campaignDetails.tabs.kpiGoals' | translate
            }}</span>
          </ng-template>
          <div class="pt-2" [class.overflow-hidden]="store.limitReached$()">
            <app-campaign-details-goals
              data-testid="campaign-details-tab-panel-kpi-goals"
              class="block w-fill-available"
              [analyticsNodes]="deliverablesAnalyticsNodes()"
              [campaign]="store.campaignApi$()"
              [campaignAnalytics]="store.campaignAnalyticsShell$()"
              [campaignAnalyticsFilter]="campaignAnalyticsFilter()"
              [campaignExportConfig]="campaignExportConfig()"
              [exportMode]="exportMode()"
              [loading]="!store.deliverablesState$().data || store.deliverablesState$().inFlight"
              [loadError]="!store.deliverablesState$().data && !!store.deliverablesState$().error"
              [deepLink]="goalsDeepLink()"
              [canOpenContents]="visibleTabs().includes(ECampaignDetailsTab.contents)"
              [canOpenDeliverables]="visibleTabs().includes(ECampaignDetailsTab.deliverables)"
              (analyticsRequested)="onGoalsAnalyticsRequested($event)"
              (contentsRequested)="onCreatorsDeepLinkClicked($event)"
              (deliverablesRequested)="onCreatorsDeepLinkClicked($event)"
            />
          </div>
        </mat-tab>
      }

      @if (visibleTabs().includes(ECampaignDetailsTab.performance)) {
        <mat-tab [disabled]="store.limitReached$()">
          <ng-template mat-tab-label>
            <span class="text-base" data-testid="campaign-details-tab-performance">{{
              'pages.campaignDetails.tabs.kpis' | translate
            }}</span>
          </ng-template>
          <div class="pt-2 min-h-[calc(100vh-400px)]" [class.overflow-hidden]="store.limitReached$()">
            <app-campaign-details-kpis
              data-testid="campaign-details-tab-panel-performance"
              [matchedKpis]="matchedKpis()"
              [loading]="kpiLoading()"
              [campaignId]="campaignId"
              [exportMode]="exportMode()"
              [campaignExportId]="store.campaignExport$()?.id ?? null"
              [exportCampaignId]="store.campaignExport$()?.campaignId ?? null"
              [campaignPlatformFeatures]="kpiPlatformFeatures()"
              [campaignAnalyticsFilter]="campaignAnalyticsFilter()"
            />
          </div>
        </mat-tab>
      }

      @if (visibleTabs().includes(ECampaignDetailsTab.deliverables)) {
        <mat-tab [disabled]="store.limitReached$()">
          <ng-template mat-tab-label>
            <span class="text-base" data-testid="campaign-details-tab-deliverables">{{
              'pages.campaignDetails.tabs.deliverables' | translate
            }}</span>
          </ng-template>
          <div class="pt-2" [class.overflow-hidden]="store.limitReached$()">
            <app-campaign-details-deliverables
              data-testid="campaign-details-tab-panel-deliverables"
              class="block w-fill-available"
              [campaignExportConfig]="campaignExportConfig()"
              [analyticsNodes]="deliverablesAnalyticsNodes()"
              [loading]="store.deliverablesState$().inFlight"
              [deepLinkFilters]="deepLinkDeliverablesFilters()"
              [filteredCreator]="deepLinkCreatorDeliverables()"
              (deliverableRowGroupClicked)="onDeliverableRowGroupClick($event)"
              (deliverableRowClicked)="onDeliverableRowClick($event)"
              (filteredCreatorRemoved)="onFilteredCreatorRemovedDeliverables()"
              (analyticsFilterChanged)="onAnalyticsFilterChanged($event, ECampaignDetailsTab.deliverables)"
            />
          </div>
        </mat-tab>
      }

      @if (visibleTabs().includes(ECampaignDetailsTab.observations)) {
        <mat-tab [disabled]="store.limitReached$()">
          <ng-template mat-tab-label>
            <span class="text-base" data-testid="campaign-details-tab-observations">{{
              'pages.campaignDetails.tabs.observations' | translate
            }}</span>
          </ng-template>
          <div class="pt-2" [class.overflow-hidden]="store.limitReached$()">
            <app-campaign-details-observations
              data-testid="campaign-details-tab-panel-observations"
              class="block w-fill-available"
              [campaignExportConfig]="campaignExportConfig()"
              [analyticsNodes]="deliverablesAnalyticsNodes()"
              [loading]="store.deliverablesState$().inFlight"
              (observationRowClicked)="onObservationRowClick($event)"
              (analyticsFilterChanged)="onAnalyticsFilterChanged($event, ECampaignDetailsTab.observations)"
            />
          </div>
        </mat-tab>
      }

      @if (visibleTabs().includes(ECampaignDetailsTab.contents)) {
        <mat-tab [disabled]="store.limitReached$()">
          <ng-template mat-tab-label>
            <span class="text-base" data-testid="campaign-details-tab-contents">{{
              'pages.campaignDetails.tabs.contents' | translate
            }}</span>
          </ng-template>
          <div class="pt-2" [class.overflow-hidden]="store.limitReached$()">
            <app-social-content-tiles-list
              data-testid="campaign-details-tab-panel-contents"
              class="w-fill-available"
              [analyticsNodes]="deliverablesAnalyticsNodes()"
              [campaignExport]="store.campaignExport$()"
              [campaignExportConfig]="campaignExportConfig()"
              [exportMode]="exportMode()"
              [loading]="!store.deliverablesState$().data || store.deliverablesState$().inFlight"
              [filterSignature]="store.deliverablesState$().filterSignature"
              [hasQueryError]="!!store.deliverablesState$().error"
              [desiredFilterSignature]="store.currentFilterSignature$()"
              [deepLinkFilters]="deepLinkContentsFilters()"
              [filteredCreator]="deepLinkCreatorContents()"
              [filteredGroup]="deepLinkGroupContents()"
              (tileClicked)="onTileClick($event)"
              (filteredCreatorRemoved)="onFilteredCreatorRemovedContents()"
              (filteredGroupRemoved)="onFilteredGroupRemovedContents()"
            />
          </div>
        </mat-tab>
      }

      @if (visibleTabs().includes(ECampaignDetailsTab.creators)) {
        <mat-tab [disabled]="store.limitReached$()">
          <ng-template mat-tab-label>
            <span class="text-base" data-testid="campaign-details-tab-creators">{{
              'pages.campaignDetails.tabs.creators' | translate
            }}</span>
          </ng-template>
          <div class="pt-7 min-h-[calc(100vh-400px)]" [class.overflow-hidden]="store.limitReached$()">
            <app-campaign-details-creators
              data-testid="campaign-details-tab-panel-creators"
              [ctaKpiStatusData]="store.ctaKpiStatusState$().data"
              [campaignAnalyticsApi]="store.campaignAnalyticsApi$()"
              [analyticsNodes]="store.deliverablesState$().data"
              [campaign]="store.campaignApi$()"
              [selectedPlatformFeatures]="campaignAnalyticsFilter().platformFeatures"
              [selectedCreatorIds]="campaignAnalyticsFilter().creatorIds"
              [loading]="
                !store.campaignApi$() || !store.ctaKpiStatusState$().data || store.ctaKpiStatusState$().inFlight
              "
              [creatorCampaignExports]="creatorCampaignExports()"
              [exportMode]="exportMode()"
              [campaignExport]="store.campaignExport$()"
              (deepLinkClicked)="onCreatorsDeepLinkClicked($event)"
            />
          </div>
        </mat-tab>
      }
    </mat-tab-group>
  }
</app-content-container>
